export type ApiModel = {
  id: string;
  name: string;
  version: string;
  displayName: string;
};

export const buildModelDisplayLabel = (name: string, version?: string) =>
  version ? `${name} v${version}` : name;

export const normalizeApiModel = (item: any): ApiModel => {
  if (typeof item === "string") {
    return {
      id: item,
      name: item,
      version: "",
      displayName: item,
    };
  }

  const name = item.name || item.modelName || String(item.id || item.modelId || "");
  const version = item.version || item.modelVersion || "";
  const id =
    item.modelId != null && item.modelId !== ""
      ? String(item.modelId)
      : item.id != null && item.id !== ""
      ? String(item.id)
      : `${name}::${version}`;

  return {
    id,
    name,
    version,
    displayName: buildModelDisplayLabel(name, version),
  };
};

export const matchesModelPrediction = (
  entry: any,
  model: { id: string; name: string; version?: string }
) => {
  if (!entry || !model) return false;
  if (entry.modelId != null && String(entry.modelId) === model.id) {
    return true;
  }
  const entryName = entry.modelName || entry.name;
  const entryVersion = entry.modelVersion || entry.version || "";
  const modelVersion = model.version || "";
  return entryName === model.name && String(entryVersion) === String(modelVersion);
};

export const getLatestModelPredictionEntry = (media: any): any | null => {
  const entries = media?.modelPredictions;
  if (!Array.isArray(entries) || entries.length === 0) {
    return null;
  }
  if (entries.length === 1) {
    return entries[0];
  }
  return entries.reduce((latest: any, entry: any) => {
    const latestAt = latest?.uploadedAt || "";
    const entryAt = entry?.uploadedAt || "";
    return entryAt > latestAt ? entry : latest;
  }, entries[0]);
};

export const extractVideoLabelsFromLatestModelPrediction = (
  media: any
): any[] => {
  const match = getLatestModelPredictionEntry(media);
  if (!match) {
    return [];
  }
  if (Array.isArray(match.videoLabels)) {
    return match.videoLabels;
  }
  if (Array.isArray(match.labels?.videoLabels)) {
    return match.labels.videoLabels;
  }
  return [];
};

export const extractVideoLabelsFromModelPrediction = (
  media: any,
  model: { id: string; name: string; version?: string }
): any[] => {
  const entries = media?.modelPredictions;
  if (!Array.isArray(entries) || !model) return [];

  const match = entries.find((entry: any) => matchesModelPrediction(entry, model));
  if (!match) return [];

  if (Array.isArray(match.videoLabels)) return match.videoLabels;
  if (Array.isArray(match.labels?.videoLabels)) return match.labels.videoLabels;
  if (Array.isArray(match.labels)) return match.labels;
  return [];
};

export const toModelPredictionUploadBody = (payload: {
  modelName: string;
  modelVersion: string;
  projectId: string;
  batchId: number;
  predictions: any[];
}) => ({
  modelName: payload.modelName,
  modelVersion: payload.modelVersion,
  projectId: payload.projectId,
  batchId: payload.batchId,
  predictions: (payload.predictions || []).map((item) => ({
    id: String(item.id),
    labels: item.labels,
  })),
});
